import apiClient from "./apiClient";

// expira en 10 minutos
const CACHE_DURATION = 10 * 60 * 1000; 

const isCacheValid = (timestamp) => {
  if (!timestamp) return false;
  const now = Date.now();
  const lastUpdate = parseInt(timestamp);
  return (now - lastUpdate) < CACHE_DURATION;
};

const invalidateCache = () => {
  localStorage.removeItem('userOrders');
  localStorage.removeItem('adminOrders');
  localStorage.removeItem('ordersTimestamp');
};

export const getUserOrders = async () => {
  try {
    const cachedData = localStorage.getItem('userOrders');
    const cacheTimestamp = localStorage.getItem('ordersTimestamp');
    
    // Verificar si el caché es válido y existe
    if (cachedData && cacheTimestamp && isCacheValid(cacheTimestamp)) {
      console.log('Usando caché de órdenes, tiempo restante:', 
        Math.round((CACHE_DURATION - (Date.now() - parseInt(cacheTimestamp))) / 1000), 'segundos');
      return JSON.parse(cachedData);
    }

    console.log('Caché expirado o no existe, obteniendo datos frescos');
    
    // Si no hay caché válido, hacer petición
    const response = await apiClient.get('/orders/my-orders/', {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
      }
    });
    
    console.log('User orders response:', response.data);
    
    // Handle different response formats
    let ordersData;
    if (response.data && response.data.results) {
      // Paginated response
      ordersData = response.data.results;
    } else if (response.data && response.data.orders) {
      // Non-paginated response with custom structure
      ordersData = response.data.orders;
    } else if (Array.isArray(response.data)) {
      // Direct array response
      ordersData = response.data;
    } else {
      ordersData = [];
    }
    
    // Guardar en caché con timestamp actual
    localStorage.setItem('userOrders', JSON.stringify(ordersData));
    localStorage.setItem('ordersTimestamp', Date.now().toString());
    
    console.log('Final orders data being returned:', ordersData);
    return ordersData;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al obtener las órdenes');
  }
};

export const getOrders = async (ignoreCache = false) => {
  try {
    const cachedData = localStorage.getItem('adminOrders');
    const cacheTimestamp = localStorage.getItem('ordersTimestamp');
    
    // Si ignoreCache es true o el caché no es válido, hacer petición nueva
    if (!ignoreCache && cachedData && cacheTimestamp && isCacheValid(cacheTimestamp)) {
      return JSON.parse(cachedData);
    }

    const response = await apiClient.get("/orders/", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
      }
    });
    
    console.log('Admin orders response:', response.data);
    
    // Handle paginated response - extract results array
    let ordersData;
    if (response.data && response.data.results) {
      ordersData = response.data.results;
    } else if (Array.isArray(response.data)) {
      ordersData = response.data;
    } else {
      ordersData = [];
    }
    
    localStorage.setItem('adminOrders', JSON.stringify(ordersData));
    localStorage.setItem('ordersTimestamp', Date.now().toString());
    
    return ordersData;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al obtener las órdenes');
  }
};

export const createOrder = async (orderData) => {
  try {
    const token = localStorage.getItem('accessToken');
    // Ensure the payload matches the expected format
    const payload = {
      recipient_name: orderData.recipient_name, // Make sure this is included
      shipping_address: orderData.shipping_address,
      customer_email: orderData.customer_email,
      customer_phone: orderData.customer_phone,
      delivery_type: orderData.delivery_type,
      products: orderData.products.map(item => ({
        product_id: item.product_id,
        blister_id: item.blister_id,
        quantity: item.quantity
      }))
    };

    const response = await apiClient.post('/orders/', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    invalidateCache();
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al crear la orden');
  }
};

export const getOrderDetails = async (orderId) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.get(`/orders/${orderId}/`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al obtener los detalles de la orden');
  }
};

export const getOrder = async (id) => {
  apiClient.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem('accessToken')}`;
  const response = await apiClient.get(`/orders/${id}/`);
  return response.data;
};

export const updateOrder = async (orderId, orderData) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/orders/${orderId}/`, orderData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    // Invalidar todos los cachés al actualizar una orden
    invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error en updateOrder:', error.response?.data);
    throw new Error(error.response?.data?.message || 'Error al actualizar el pedido');
  }
};

export const deleteOrder = async (id) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.delete(`/orders/${id}/`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    // Invalidate cache after deletion
    invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error deleting order:', error.response?.data);
    throw new Error(error.response?.data?.message || 'Error al eliminar el pedido');
  }
};

export const toggleArchiveOrder = async (orderId, forceArchive = null) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.post(`/orders/${orderId}/toggle-archive/`, 
      { force_archive: forceArchive },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    
    // Invalidate cache to reflect changes
    invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error toggling archive status:', error.response?.data);
    throw new Error(error.response?.data?.message || 'Error al cambiar el estado de archivo del pedido');
  }
};
